import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Sprout,
  Linkedin,
  Twitter,
  Mail,
  ArrowRight,
  MapPin,
  Sparkles,
} from "lucide-react";
import { NAV_LINKS, BRAND } from "../../data/sparkseed";

const OFFICES = [
  { city: "Bengaluru", line: "HQ · Investments" },
];

const LEGAL = ["Privacy", "Disclosures", "Terms", "SEBI Disclaimer"];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribed(true);
    setTimeout(() => {
      setSubscribed(false);
      setEmail("");
    }, 2500);
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0F2818] text-white overflow-hidden"
    >
      {/* Top accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#B8D958] to-transparent opacity-60" />

      {/* Soft glow */}
      <div className="absolute left-1/2 -top-40 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#B8D958]/8 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 right-0 w-[500px] h-[400px] rounded-full bg-[#E8A33D]/6 blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* ============ Newsletter strip ============ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="relative mt-4 mb-14 rounded-2xl overflow-hidden border border-[#B8D958]/20 bg-[#F7F8EF]"
        >
          {/* Lime side accent */}
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-[#B8D958] via-[#E8A33D] to-[#B8D958]" />

          <div className="relative pl-7 pr-5 sm:pl-10 sm:pr-8 py-6 sm:py-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
            {/* Icon */}
            <div className="flex items-start gap-4 lg:flex-1 min-w-0">
              <div className="h-12 w-12 rounded-xl bg-[#14401F] flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-[#B8D958]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#14401F] mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-[#E8A33D]" />
                  Quarterly LP Briefing
                </div>
                <div className="font-serif-display text-xl sm:text-2xl font-bold text-[#0F2818] leading-tight">
                  Get our deal-flow{" "}
                  <span className="italic text-[#14401F]">memo</span> before
                  the market.
                </div>
                <div className="mt-1 text-xs text-[#4A5A4F]">
                  2,400+ LPs · 4-min read · No spam
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={subscribe}
              data-testid="footer-newsletter-form"
              className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0 lg:w-auto"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com"
                data-testid="footer-newsletter-input"
                className="w-full sm:w-64 rounded-full bg-white border border-[#14401F]/15 px-5 py-3 text-sm text-[#0F2818] placeholder:text-[#4A5A4F]/60 focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/30 transition-all"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                disabled={subscribed}
                className="group inline-flex items-center justify-center gap-2 bg-[#14401F] hover:bg-[#0F2818] disabled:opacity-80 text-[#B8D958] font-semibold rounded-full px-6 py-3 text-sm transition-all whitespace-nowrap"
              >
                {subscribed ? (
                  "Subscribed ✓"
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>

        {/* ============ Main footer grid ============ */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 pb-14">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <img
              src="/sparkseed-logo-clean.png"
              alt="SparkSeed Ventures"
              className="h-14 w-auto mb-5"
              style={{ filter: "brightness(1.1)" }}
            />
            <p className="text-white/55 text-sm leading-relaxed max-w-sm">
              From First Spark to Final Scale.
            </p>

            <div className="flex gap-2 mt-7">
              {[
                { I: Linkedin, label: "LinkedIn", href: "#" },
                { I: Twitter, label: "Twitter", href: "#" },
                { I: Mail, label: "Email", href: "mailto:investments@sparkseedventures.in" },
              ].map(({ I, label, href }, i) => (
                <a
                  key={i}
                  href={href}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:bg-[#B8D958] hover:text-[#0F2818] hover:border-[#B8D958] transition-all hover:-translate-y-0.5"
                >
                  <I className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] font-bold mb-5">
              Explore
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    className="text-sm text-white/65 hover:text-[#B8D958] transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="h-px w-0 group-hover:w-3 bg-[#B8D958] transition-all" />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] font-bold mb-5">
              Get in touch
            </div>
            <a
              href="mailto:investments@sparkseedventures.in"
              className="block text-base font-medium text-white hover:text-[#B8D958] transition-colors"
            >
              investments@sparkseedventures.in
            </a>
            <a
              href="#"
              className="block text-sm text-white/55 hover:text-[#B8D958] transition-colors mt-1"
            >
              {BRAND.website}
            </a>
            <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-white/40 font-medium">
              For founders
            </div>
            <div className="text-sm text-white/65">c@sparkseedventures.in</div>
            <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40 font-medium">
              For LPs
            </div>
            <div className="text-sm text-white/65">mayur@sparkseedventures.in</div>
          </div>

          {/* Offices */}
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] font-bold mb-5">
              Offices
            </div>
            <ul className="space-y-4">
              {OFFICES.map((o) => (
                <li key={o.city} className="flex items-start gap-3">
                  <span className="mt-0.5 h-7 w-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-3.5 w-3.5 text-[#B8D958]" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-white">{o.city}</div>
                    <div className="text-[11px] text-white/50">{o.line}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ============ Bottom strip ============ */}
        <div className="border-t border-white/10 py-6 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="text-xs text-white/40">
            © 2026 SparkSeed Ventures
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL.map((l) => (
              <a
                key={l}
                href="#"
                className="text-xs text-white/50 hover:text-[#B8D958] transition-colors"
              >
                {l}
              </a>
            ))}
          </div>
          <div className="text-[11px] italic text-white/35">
            Private & Confidential — for prospective LPs only
          </div>
        </div>

        {/* ============ Giant brand wordmark ============ */}
      </div>

      {/* Wordmark - full width, no padding constraint */}
      <div className="relative pb-10 pt-2 select-none overflow-hidden">
        <div
          className="font-serif-display font-black text-center whitespace-nowrap mx-auto"
          style={{
            fontSize: "clamp(2.5rem, 11vw, 11rem)",
            letterSpacing: "-0.04em",
            lineHeight: "0.9",
            color: "rgba(184,217,88,0.14)",
            WebkitTextStroke: "0.5px rgba(184,217,88,0.22)",
          }}
        >
          SPARKSEED
        </div>
        {/* Tagline beneath — tightly attached */}
        <div className="text-center mt-1 px-6">
          <span className="text-[10px] uppercase tracking-[0.5em] text-[#B8D958]/55 font-semibold">
            This isn&apos;t investment. This is a movement.
          </span>
        </div>
      </div>
    </footer>
  );
}
